import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { SALES_ITEM_BOOKING_FLOW_TYPE } from 'noddi-types';
import { NoddiBasicCard, NoddiCircularLoader } from 'noddi-ui-web';
import { getCarDisplayName } from 'noddi-util';
import {
  useBookingAddress,
  useBookingCars,
  useBookingSelectedSalesItemsCars,
  useLocalizationLanguage
} from 'noddi-web';
import { useMobileBookingCustomer } from '../../../hooks/useMobileBookingCustomer';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { ApiErrorMessageWithTrans } from '../../Error/ApiErrorMessageWithTrans';
import { BookingValidationDialog } from '../Common/BookingValidationDialog';
import { isExpectedInitialAvailableSalesItemsOutcome } from './initialAvailableSalesItemsCustomerError';
import { NoAvailableServicesMessage } from './NoAvailableServicesMessage';

export type BookingValidationVariant =
  | 'addressRequired'
  | 'carSelectionRequired'
  | 'serviceSelectionRequired'
  | 'tireHotelPickupRequired'
  | 'tireHotelSeasonRequired'
  | 'timeSlotRequired'
  | 'insuranceInfoRequired'
  | 'stepIncomplete';

interface ValidationModalProps {
  open: boolean;
  onClose: () => void;
  variant: BookingValidationVariant;
}

export const BookingValidationModal = ({ open, onClose, variant }: ValidationModalProps) => {
  const { userGroupId } = useMobileBookingCustomer();
  const { currentBrand } = useCurrentBrand();
  const language = useLocalizationLanguage();

  // Additional data for detailed error messages
  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();

  const selectedAddress = useBookingAddress();
  const selectedCars = useBookingCars();

  const {
    data: carsWithSalesItems,
    // need to use loading, not pending here
    isLoading: carsWithSalesItemsLoading,
    error: salesItemsError
  } = noddiAsync.usePostAsGetQuery({
    type: URLKeys.getInitialAvailableSalesItems,
    input: {
      userGroupId,
      addressId: selectedAddress?.id as number,
      licensePlates: selectedCars.map((car) => ({
        number: car.licensePlate.number,
        countryCode: car.licensePlate.countryCode
      })),
      brandDomain: currentBrand,
      language
    },
    queryConfig: {
      enabled:
        open && variant === 'serviceSelectionRequired' && !!selectedAddress && selectedCars.length > 0 && !!currentBrand
    }
  });

  const getValidationMessage = (): {
    title?: string;
    message?: string;
    content?: React.ReactElement;
    node?: React.ReactElement;
  } => {
    if (variant === 'addressRequired') {
      return {
        title: t`Address required`,
        message: t`Please select an address before proceeding to the next step.`
      };
    }

    if (variant === 'carSelectionRequired') {
      return {
        title: t`At least one car required`,
        message: t`Please add at least one car to your booking before proceeding.`
      };
    }

    const carsWithoutServices = selectedCars.filter((car) => {
      return !selectedSalesItemsCars.some(
        (item) =>
          item.licensePlate.number === car.licensePlate.number &&
          item.licensePlate.countryCode === car.licensePlate.countryCode
      );
    });

    if (variant === 'serviceSelectionRequired') {
      if (carsWithSalesItemsLoading) {
        return { content: <NoddiCircularLoader /> };
      }

      if (salesItemsError) {
        // Both branches below can render the red API-failure treatment, so the title has to follow
        // the body — an empty catalog is "no services", anything else is a failure.
        const isEmptyCatalog =
          !!selectedAddress && selectedCars.length > 0 && isExpectedInitialAvailableSalesItemsOutcome(salesItemsError);

        return {
          title: isEmptyCatalog ? t`No services available` : t`Something went wrong`,
          content:
            selectedAddress && selectedCars.length > 0 ? (
              <NoAvailableServicesMessage
                error={salesItemsError}
                carCount={selectedCars.length}
                addressId={selectedAddress.id}
                licensePlateNumbers={selectedCars.map((c) => c.licensePlate.number)}
              />
            ) : (
              <ApiErrorMessageWithTrans error={salesItemsError} />
            )
        };
      }

      if (carsWithoutServices.length > 0) {
        return {
          title: t`At least one service required for each car`,
          message: t`Please add at least one of the available services for the following cars or remove the cars from your booking:`,
          node: (
            <div className='flex flex-col'>
              <p className='text-sm'>{t`Cars without services:`}</p>
              <div className='flex flex-col gap-2'>
                {carsWithoutServices.map((car, index) => {
                  // Find the sales items data for this car
                  const carSalesData = carsWithSalesItems?.data.cars.find(
                    (salesItemsCar) =>
                      salesItemsCar.licensePlate.number === car.licensePlate.number &&
                      salesItemsCar.licensePlate.countryCode === car.licensePlate.countryCode
                  );

                  const availableServices = carSalesData?.salesItems || [];
                  const topServices = availableServices.slice(0, 4); // Show top 4 services
                  const remainingCount = availableServices.length - 4;

                  return (
                    <NoddiBasicCard
                      size='small'
                      className='bg-slate-50 text-sm'
                      key={`${car.licensePlate.number}-${car.licensePlate.countryCode}-${index}`}
                    >
                      <div className='mb-2 font-medium text-gray-900'>{getCarDisplayName({ car })}</div>
                      {topServices.length > 0 ? (
                        <div>
                          {/* eslint-disable-next-line noddi/enforce-spacing-scale -- space-y stack not migrated to flex-col gap yet; swapping changes child margin layout */}
                          <ul className='space-y-1'>
                            {topServices.map((service, serviceIndex) => (
                              <li key={serviceIndex} className='flex items-center text-gray-700'>
                                <span className='mr-2 size-2 shrink-0 rounded-full bg-muted-foreground' />
                                {service.name}
                              </li>
                            ))}
                          </ul>
                          {remainingCount > 0 && (
                            <p className='mt-2 text-gray-500'>{t`And ${remainingCount} more services available`}</p>
                          )}
                        </div>
                      ) : (
                        <div className='rounded bg-yellow-100 p-2 text-yellow-700'>
                          {t`No services available for this vehicle`}
                        </div>
                      )}
                    </NoddiBasicCard>
                  );
                })}
              </div>
            </div>
          )
        };
      }

      return {
        title: t`At least one service required for each car`,
        message: t`Please select at least one service for each car before proceeding to the next step.`
      };
    }

    if (variant === 'tireHotelPickupRequired') {
      const carsWithWheelPickup = selectedCars.filter((car) =>
        selectedSalesItemsCars.some(
          (selectedCar) =>
            selectedCar.licensePlate.number === car.licensePlate.number &&
            selectedCar.licensePlate.countryCode === car.licensePlate.countryCode &&
            selectedCar.isWheelPickupRequired
        )
      );

      const carsWithoutPickupServices = carsWithWheelPickup.filter((car) => {
        return !selectedSalesItemsCars.some(
          (selectedCar) =>
            selectedCar.licensePlate.number === car.licensePlate.number &&
            selectedCar.licensePlate.countryCode === car.licensePlate.countryCode &&
            selectedCar.type === SALES_ITEM_BOOKING_FLOW_TYPE.TIRE_HOTEL_PICKUP
        );
      });

      if (carsWithoutPickupServices.length > 0) {
        return {
          title: t`Tire hotel pickup service required`,
          message: t`Please select tire hotel pickup options for the following cars or remove the cars from your booking. Choose whether we should pick up tires from your address or from another address.`,
          node: (
            <div className='flex flex-col'>
              <p className='text-sm'>
                <Trans>Please select tire hotel pickup options for the following cars</Trans>
              </p>
              <div className='flex flex-col gap-2'>
                {carsWithoutPickupServices.map((car, index) => (
                  <NoddiBasicCard
                    size='small'
                    className='bg-slate-50 text-sm'
                    key={`${car.licensePlate.number}-${car.licensePlate.countryCode}-${index}`}
                  >
                    <div className='mb-2 font-medium text-gray-900'>{getCarDisplayName({ car })}</div>
                  </NoddiBasicCard>
                ))}
              </div>
            </div>
          )
        };
      }

      return {
        title: t`Tire hotel pickup selection required`,
        message: t`Please select tire hotel pickup options for the following cars or remove the cars from your booking. Choose whether we should pick up tires from your address or from another address.`
      };
    }

    if (variant === 'tireHotelSeasonRequired') {
      return {
        title: t`Choose which tires we store`,
        message: t`Choose winter or summer tires before you continue.`
      };
    }

    if (variant === 'timeSlotRequired') {
      return {
        title: t`Time slot required`,
        message: t`Please select one of the available time slots for your booking before proceeding.`
      };
    }

    if (variant === 'insuranceInfoRequired') {
      return {
        title: t`Insurance information required`,
        message: t`Please complete all required insurance information before proceeding to the next step.`
      };
    }

    // Generic fallback message
    return {
      title: t`Step incomplete`,
      message: t`Please complete all required fields before proceeding to the next step.`
    };
  };

  const { title, message, content, node } = getValidationMessage();

  return (
    <BookingValidationDialog open={open} onClose={onClose} title={title} message={message}>
      {content ?? node}
    </BookingValidationDialog>
  );
};
